import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Typography,
} from '@mui/material';
import { ReactComponent as InfoIcon } from '../../../assets/icons/ic_action_info_outlined.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/ic_action_location_marker_outlined.svg';
import { ReactComponent as ShowOutlinedIcon } from '../../../assets/icons/ic_action_show_outlined.svg';
import MenuItemWithLines from '../../components/MenuItemWithLines';
import DefaultItem from '../../../frame/components/items/DefaultItem';
import styles from '../search.module.css';
import dialogStyles from '../../../dialogs/dialog.module.css';
import { useTranslation } from 'react-i18next';
import capitalize from 'lodash-es/capitalize';
import {
    formattingPoiType,
    getFirstSubstring,
    hasTypeTranslation,
    navigateToPoi,
    preparedType,
} from '../../../manager/PoiManager';
import AppContext, { OBJECT_SEARCH, OBJECT_TYPE_CLOUD_TRACK, OBJECT_TYPE_POI } from '../../../context/AppContext';
import {
    FAVORITE_HIT_GROUP_ID,
    searchTypeMap,
    WPT_TRACK_FILE,
    WPT_TRACK_SHARED,
} from '../../../manager/searchConstants';
import { createSearchMatchedObjectActions } from '../../../manager/SpatialSearchMatchedObjects';
import DistanceInfo from '../../../infoblock/components/common/DistanceInfo';
import { getDistance, getBearing } from '../../../util/Utils';
import {
    ADDRESS_1,
    ADDRESS_2,
    BACKGROUND_TYPE_EXTENSION,
    CATEGORY_KEY_NAME,
    CATEGORY_NAME,
    CATEGORY_TYPE,
    CITY,
    COLOR_NAME_EXTENSION,
    EN_NAME,
    ICON_KEY_NAME,
    MATCHED_ALT_NAME,
    POI_NAME,
    POI_SUBTYPE,
    POI_TYPE,
    SEPARATOR,
    TYPE,
    WEB_PREFIX,
} from '../../../infoblock/components/wpt/WptTagsProvider';
import { getObjIdSearch, getPoiParentCategory, openTrackWptFromSearch } from '../../../manager/SearchManager';
import { LatLng } from 'leaflet';
import { POI_LAYER_ID } from '../../../manager/GlobalManager';
import DividerWithMargin from '../../../frame/components/dividers/DividerWithMargin';
import { apiGet } from '../../../util/HttpApi';
import useSearchNav from '../../../util/hooks/search/useSearchNav';
import { POI_OBJECTS_KEY, useRecentDataSaver } from '../../../util/hooks/menu/useRecentDataSaver';
import i18n from 'i18next';
import { useNavigate } from 'react-router-dom';
import { openTrackOnMap, updateTracks } from '../../../manager/track/TracksManager';
import { getTrackInfoText } from '../../tracks/CloudTrackItem';
import {
    addFavoriteToMapFromSearch,
    getFavoriteMenuIconHtml,
    resolveFavoriteMarkerForSearch,
} from '../../../manager/FavoritesManager';
import FavoriteItem from '../../favorite/FavoriteItem';

export function getPropsFromSearchResultItem(props, t = null, lang = null, listFiles = null, unitsSettings = null) {
    let restoreLang;
    if (t && lang) {
        restoreLang = i18n.language;
        i18n.changeLanguage(lang);
    }
    let type, name, city;
    if (props[CATEGORY_TYPE] === searchTypeMap.POI || !props[CATEGORY_TYPE]) {
        name = props[POI_NAME];
        const poiSubType = getFirstSubstring(props[POI_SUBTYPE]);
        const poiType = getFirstSubstring(props[POI_TYPE]);
        type = poiSubType ?? poiType;
        if (name === '' && type) {
            name = preparedType(type, t);
            type = poiType;
        }
        // an untranslated type would be rendered as its own key
        type = type && hasTypeTranslation(type) ? preparedType(type, t) : undefined;
    } else if (props[CATEGORY_TYPE] === searchTypeMap.FAVORITE) {
        name = props[POI_NAME] ?? props[CATEGORY_NAME];
        type = t ? t('shared_string_my_favorites') : '';
    } else if (props[CATEGORY_TYPE] === searchTypeMap.GPX_TRACK) {
        name = props[CATEGORY_NAME];
    } else if (props[CATEGORY_TYPE] === searchTypeMap.WPT) {
        name = props[POI_NAME];
        type = props[CATEGORY_NAME];
    } else {
        name = props[CATEGORY_NAME];
        if (props[CATEGORY_TYPE] === searchTypeMap.POI_TYPE) {
            type = getPoiParentCategory(props, t);
        } else {
            type = props[CATEGORY_TYPE]?.toLowerCase();
            if (type) {
                const typeKey = type === 'postcode' ? 'postcode' : `city_type_${type}`;
                type = capitalize(t([typeKey, `search_address_${type}`], formattingPoiType(type)));
            }
        }
    }

    name = getFirstSubstring(name);
    type = getFirstSubstring(type);
    city = props[CITY];
    const altName = props[MATCHED_ALT_NAME] !== name ? props[MATCHED_ALT_NAME] : undefined;

    const addressParts = [props[ADDRESS_1], props[ADDRESS_2]].filter(Boolean);
    const info = getInfo();

    function getInfo() {
        if (props[CATEGORY_TYPE] === searchTypeMap.GPX_TRACK) {
            return getTrackInfo(name, listFiles, unitsSettings, t);
        }
        if (addressParts.length > 0) {
            if (type.toLowerCase() === searchTypeMap.STREET.toLowerCase()) {
                return addressParts[0];
            }
            return addressParts.join(', ');
        }
        return undefined;
    }

    if (restoreLang) {
        i18n.changeLanguage(restoreLang);
    }

    return { name, type, info, city, altName };
}

function getTrackInfo(name, listFiles, unitsSettings, t) {
    if (!listFiles || !unitsSettings) {
        return '';
    }

    const file = listFiles.uniqueFiles?.find((f) => f.name === name);

    return getTrackInfoText(file, unitsSettings, t);
}

function safeCategoryTypeKey(type) {
    return String(type).replaceAll(/[^a-zA-Z0-9_-]/g, '_');
}

export default function SearchResultItem({ item, typeItem, index, currentLoc, loc = null, isUser = false }) {
    const ctx = useContext(AppContext);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const { name, altName, info, distance, bearing, isUserLocation, type, city, icon } = parseItem(item);
    const [isHovered, setIsHovered] = useState(false);
    const [showMatched, setShowMatched] = useState(false);
    const [showPropertiesDump, setShowPropertiesDump] = useState(false);

    const { navigateToSearchResults } = useSearchNav();
    const recentSaver = useRecentDataSaver();
    const backToSearchResultsState = { state: { backToSearchResults: true } };
    const { matchedObjects, matchedNameObjects, matchedDialogObjects, moveToMatchedPoiTypeLocation } =
        createSearchMatchedObjectActions({
            item,
            t,
            ctx,
            navigate,
            recentSaver,
            setShowMatched,
            formatSearchResultProperties: getPropsFromSearchResultItem,
            navigateToPoi,
            objectSearchType: OBJECT_SEARCH,
            poiObjectsKey: POI_OBJECTS_KEY,
            poiTypeCategory: searchTypeMap.POI_TYPE,
        });
    const itemId = getObjIdSearch(item);

    function handleMouseEnter() {
        if (itemId !== null) {
            setSelectedPoint({ show: true });
        }
        setIsHovered(true);
    }

    function handleMouseLeave() {
        if (itemId !== null) {
            setSelectedPoint({ show: false });
        }
        setIsHovered(false);
    }

    function setSelectedPoint({ show }) {
        ctx.setSelectedWptId({ id: itemId, show, type: typeItem, obj: item, prev: ctx.selectedWptId });
    }

    useEffect(() => {
        const hoverIds = [ctx.selectedWptId?.id, ...(ctx.selectedWptId?.relatedResultIds ?? [])];
        setIsHovered(ctx.selectedWptId?.show !== false && hoverIds.includes(itemId));
    }, [ctx.selectedWptId?.id, ctx.selectedWptId?.relatedResultIds, ctx.selectedWptId?.show, itemId]);

    function parseItem(item) {
        const res = getPropsFromSearchResultItem(item.properties, t, null, ctx.listFiles, ctx.unitsSettings);
        const lat = item.geometry?.coordinates?.[1];
        const lon = item.geometry?.coordinates?.[0];
        const hasCoords = loc && lat != null && lon != null && !(lat === 0 && lon === 0);
        const distance = hasCoords ? getDistance(loc.lat, loc.lng, lat, lon) : null;
        const bearing = hasCoords ? getBearing(loc.lat, loc.lng, lat, lon) : null;
        const icon = item.icon;
        return { ...res, icon, distance, bearing, isUserLocation: isUser };
    }

    const id = (() => {
        const qType = ctx.searchQuery?.type;
        if (qType != null && qType !== '' && index != null) {
            return `se-search-result-item-${safeCategoryTypeKey(qType)}-${index}`;
        }
        const categoryType = item.properties[CATEGORY_TYPE];
        if (categoryType === searchTypeMap.FAVORITE) {
            return `se-search-result-fav-${name}`;
        }
        if (categoryType === searchTypeMap.WPT) {
            return `se-search-result-wpt-${name}`;
        }
        if (categoryType === searchTypeMap.POI_TYPE || categoryType === searchTypeMap.GPX_TRACK) {
            return `se-search-result-${item.properties[CATEGORY_NAME]}`;
        }
        return 'se-search-result-item';
    })();

    async function clickHandler(event) {
        if (item.properties?.[CATEGORY_TYPE] === searchTypeMap.GPX_TRACK) {
            const fileName = item.properties?.[CATEGORY_NAME];
            const file = ctx.listFiles?.uniqueFiles?.find((f) => f?.name === fileName);
            if (!file) return;
            const searchFile = {
                name: file.name,
                type: file.type,
                clienttimems: file.clienttimems,
                updatetimems: file.updatetimems,
                mapObj: false,
            };
            ctx.setSelectedSearchObj({ type: OBJECT_TYPE_CLOUD_TRACK, object: searchFile });
            const newTracks = await openTrackOnMap({
                file: searchFile,
                showOnMap: true,
                showInfo: true,
                zoomToTrack: true,
                ctx,
                recentSaver,
                fromSearch: true,
            });
            updateTracks(ctx, null, newTracks);
            return;
        }
        if (item.geometry.coordinates[0] !== 0 && item.geometry.coordinates[1] !== 0) {
            const type = item.properties[WEB_PREFIX + TYPE];
            let options;
            if (type === searchTypeMap.CITY || type === searchTypeMap.TOWN || type === searchTypeMap.VILLAGE) {
                const respPoi = await apiGet(`${process.env.REACT_APP_ROUTING_API_SITE}/search/get-poi-by-name`, {
                    apiCache: true,
                    params: {
                        lat: item.geometry.coordinates[1],
                        lon: item.geometry.coordinates[0],
                        enName: item.properties[WEB_PREFIX + EN_NAME],
                    },
                });
                if (respPoi?.data) {
                    options = respPoi.data.properties;
                }
            }
            const poi = {
                key: itemId ?? `${typeItem}-${item.geometry.coordinates[0]}-${item.geometry.coordinates[1]}`,
                options: options ?? item.properties,
                latlng: new LatLng(item.geometry.coordinates[1], item.geometry.coordinates[0]),
            };
            // click on item — navigation is handled by MainMenu via selectedType change
            ctx.setCurrentObjectType(typeItem === POI_LAYER_ID ? OBJECT_TYPE_POI : OBJECT_SEARCH);
            ctx.setSelectedPoiObj({ ...poi });
            ctx.setSelectedWpt({ poi, id: itemId });
            recentSaver(POI_OBJECTS_KEY, poi);
            const pushMapViewWithCtrlClick = Boolean(event?.ctrlKey);
            ctx.setMoveToMapObj({ ...item, pushMapViewWithCtrlClick });
            if (poi.options[CATEGORY_TYPE] === searchTypeMap.POI) {
                navigateToPoi({ poi }, navigate);
            }
        } else {
            // click on category: both engines provide the canonical key (incl. brands)
            const category = item.properties[CATEGORY_KEY_NAME];
            if (category) {
                moveToMatchedPoiTypeLocation();
                return navigateToSearchResults(
                    { type: category },
                    { state: { ...backToSearchResultsState.state, categoryName: item.properties[CATEGORY_NAME] } }
                );
            }
        }
    }

    function addInfo() {
        return info ?? '';
    }

    function addType() {
        if (
            !type ||
            type.toLowerCase() === searchTypeMap.STREET.toLowerCase() ||
            type.toLowerCase() === searchTypeMap.HOUSE.toLowerCase()
        ) {
            return '';
        }
        return `${info ? ' · ' : ''}${type}`;
    }

    function addCity() {
        if (!city) return '';
        return ` · ${city}`;
    }

    const placeDetails = `${addInfo()}${addType()}${addCity()}`;

    function hasTextBeforeMatchedName(index) {
        return index > 0 || Boolean(placeDetails || distance > 0);
    }

    if (item.properties[CATEGORY_TYPE] === searchTypeMap.FAVORITE) {
        const groupId = item.properties[FAVORITE_HIT_GROUP_ID];
        const resolved = resolveFavoriteMarkerForSearch(ctx, groupId, name);
        if (!resolved) return null;
        const marker = { ...resolved.marker, locDist: distance };
        return (
            <FavoriteItem
                id={id}
                marker={marker}
                group={resolved.group}
                currentLoc={currentLoc}
                onOpen={() => addFavoriteToMapFromSearch(ctx, { group: resolved.group, marker: resolved.marker })}
                hideActions
            />
        );
    }

    if (item.properties[CATEGORY_TYPE] === searchTypeMap.WPT) {
        return (
            <FavoriteItem
                id={id}
                marker={buildWptMarker(item, name, distance)}
                group={{ id: item.properties[WPT_TRACK_FILE], name: item.properties[CATEGORY_NAME] }}
                currentLoc={currentLoc}
                onOpen={() => {
                    openTrackWptFromSearch(ctx, {
                        file: item.properties[WPT_TRACK_FILE],
                        shared: item.properties[WPT_TRACK_SHARED],
                        name: item.properties[POI_NAME],
                        lat: item.geometry.coordinates[1],
                        lon: item.geometry.coordinates[0],
                    });
                    ctx.setMoveToMapObj({ ...item });
                }}
                hideActions
                hoverType={OBJECT_TYPE_CLOUD_TRACK}
            />
        );
    }

    return (
        <>
            <MenuItem
                id={id}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={`${styles.searchItem} ${isHovered ? styles.searchHoverItem : ''}`}
                onClick={clickHandler}
            >
                <ListItemText>
                    <MenuItemWithLines className={styles.titleText} name={name} maxLines={2}>
                        {altName && (
                            <Typography component="span" className={styles.titleText}>
                                {` (${altName})`}
                            </Typography>
                        )}
                    </MenuItemWithLines>
                    {(info ||
                        type ||
                        matchedNameObjects.length > 0 ||
                        matchedObjects.length > 1 ||
                        ctx.develFeatures) && (
                        <MenuItemWithLines className={styles.placeTypes} name={placeDetails} maxLines={4}>
                            {distance > 0 && (
                                <span style={{ display: 'inline-flex' }}>
                                    <Typography className={styles.placeDistance}>{' · '}</Typography>
                                    <DistanceInfo
                                        distance={distance}
                                        bearing={bearing}
                                        isUserLocation={isUserLocation}
                                    />
                                </span>
                            )}
                            {matchedNameObjects.map(({ key, name, onClick, onKeyDown }, i) => (
                                <React.Fragment key={key}>
                                    {hasTextBeforeMatchedName(i) && (
                                        <Typography component="span" className={styles.placeDistance}>
                                            {' · '}
                                        </Typography>
                                    )}
                                    <Typography
                                        component="span"
                                        className={styles.matchedObjectName}
                                        role="button"
                                        tabIndex={0}
                                        onClick={onClick}
                                        onKeyDown={onKeyDown}
                                    >
                                        {name}
                                    </Typography>
                                </React.Fragment>
                            ))}
                            {matchedObjects.length > 1 && (
                                <span
                                    className={styles.matchedObjectsIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMatched(true);
                                    }}
                                >
                                    <InfoIcon className={styles.placeTypesIcon} />
                                </span>
                            )}
                            {ctx.develFeatures && (
                                <span
                                    className={styles.matchedObjectsIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowPropertiesDump(true);
                                    }}
                                >
                                    <ShowOutlinedIcon className={styles.placeTypesIcon} />
                                </span>
                            )}
                        </MenuItemWithLines>
                    )}
                </ListItemText>
                <ListItemIcon className={styles.categoryItemIcon}>{icon}</ListItemIcon>
            </MenuItem>
            <DividerWithMargin margin={'16px'} />
            {showMatched && (
                <Dialog open={true} onClose={() => setShowMatched(false)} onClick={(e) => e.stopPropagation()}>
                    <DialogTitle className={dialogStyles.title}>Matched objects ({matchedObjects.length})</DialogTitle>
                    {matchedDialogObjects.map(({ key, obj, onClick }) => (
                        <DefaultItem
                            key={key}
                            icon={<LocationIcon />}
                            className={styles.matchedItem}
                            name={obj.name}
                            additionalInfo={`${obj.lat?.toFixed(5)}, ${obj.lon?.toFixed(5)}`}
                            onClick={onClick}
                        />
                    ))}
                </Dialog>
            )}
            {showPropertiesDump && (
                <Dialog
                    open={true}
                    onClose={() => setShowPropertiesDump(false)}
                    onClick={(e) => e.stopPropagation()}
                    fullWidth
                >
                    <DialogContent>
                        <Box component="pre">{JSON.stringify(item.properties, null, 2)}</Box>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

// FavoriteItem marker for a waypoint search result; the fake layer feeds the
// second line (address slot) and getFavoriteId (getLatLng)
function buildWptMarker(item, name, distance) {
    const properties = item.properties;

    return {
        name,
        icon: getFavoriteMenuIconHtml({
            icon: properties[ICON_KEY_NAME],
            color: properties[COLOR_NAME_EXTENSION],
            background: properties[BACKGROUND_TYPE_EXTENSION],
        }),
        locDist: distance,
        layer: {
            options: {
                address: properties[CATEGORY_NAME],
                color: properties[COLOR_NAME_EXTENSION],
                background: properties[BACKGROUND_TYPE_EXTENSION],
            },
            getLatLng: () => ({ lat: item.geometry.coordinates[1], lng: item.geometry.coordinates[0] }),
        },
    };
}
